import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function ProfilesCatalog() {
    const { currentUser } = useAuth();
    const isAdmin = currentUser?.role === 'admin';

    // Data state
    const [models, setModels] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    // Filter state
    const [selectedModel, setSelectedModel] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSubCat, setSelectedSubCat] = useState('all');
    const [page, setPage] = useState(1);

    // UI state
    const [selectedImage, setSelectedImage] = useState(null);
    const [expandedFormulas, setExpandedFormulas] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [editingProfile, setEditingProfile] = useState(null);
    const [formData, setFormData] = useState({ pnum: '', description: '', model: '', series: '', weightPerMeter: '', stockStatus: '' });
    const [saving, setSaving] = useState(false);

    const subCategories = useMemo(() => [
        { id: 'all', name: 'הכל', keywords: [] },
        { id: 'wings', name: 'כנפיים / דלתות', keywords: ['כנף', 'כנפיים', 'לכנף', 'דלת'] },
        { id: 'frames', name: 'משקופים (ומסילות)', keywords: ['משקוף', 'למשקוף', 'מזוזה', 'מסילה', 'תחתונה', 'עליונה'] },
        { id: 'glazing', name: 'סרגלי זיגוג', keywords: ['סרגל', 'זיגוג', 'לזיגוג'] },
        { id: 'covers', name: 'הלבשות ומכסים', keywords: ['הלבשה', 'מכסה', 'פלח'] },
        { id: 'dividers', name: 'חציצים ושולבים', keywords: ['חציץ', 'שולב'] },
    ], []);

    const token = localStorage.getItem('pro_token') || sessionStorage.getItem('pro_token') || localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
    }), [token]);

    // Fetch models list
    useEffect(() => {
        fetch(`${API_BASE}/api/profile-catalog/models`, { headers })
            .then(r => r.json())
            .then(data => {
                setModels(data);
                if (data.length > 0 && !selectedModel) {
                    setSelectedModel(data[0].model);
                }
            })
            .catch(console.error);
    }, []); // eslint-disable-line

    // Fetch profiles
    const fetchProfiles = useCallback(() => {
        if (!selectedModel) return;
        setLoading(true);
        const params = new URLSearchParams();
        params.set('model', selectedModel);
        params.set('page', page);
        params.set('limit', 200);
        if (searchQuery) params.set('search', searchQuery);

        fetch(`${API_BASE}/api/profile-catalog?${params}`, { headers })
            .then(r => r.json())
            .then(data => {
                setProfiles(data.profiles || []);
                setTotal(data.total || 0);
                setLoading(false);
            })
            .catch(err => { console.error(err); setLoading(false); });
    }, [selectedModel, searchQuery, page, headers]);

    useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

    // Apply sub-category filter client-side
    const filteredProfiles = useMemo(() => {
        if (selectedSubCat === 'all') return profiles;
        const catObj = subCategories.find(c => c.id === selectedSubCat);
        if (!catObj || catObj.keywords.length === 0) return profiles;
        return profiles.filter(p => catObj.keywords.some(kw => (p.description || '').includes(kw)));
    }, [profiles, selectedSubCat, subCategories]);

    // Toggle formula expansion
    const toggleFormulas = (pnum) => {
        setExpandedFormulas(prev => ({ ...prev, [pnum]: !prev[pnum] }));
    };

    // Image error handler
    const handleImageError = (e) => {
        e.target.src = '/assets/logo-circle.png';
        e.target.className = 'w-1/2 h-1/2 object-contain opacity-20 grayscale mx-auto mt-10';
    };

    // CRUD handlers
    const openAddModal = () => {
        setEditingProfile(null);
        setFormData({ pnum: '', description: '', model: selectedModel, series: '', weightPerMeter: '', stockStatus: '' });
        setShowModal(true);
    };

    const openEditModal = (profile) => {
        setEditingProfile(profile);
        setFormData({
            pnum: profile.pnum,
            description: profile.description,
            model: profile.model,
            series: profile.series,
            weightPerMeter: profile.weightPerMeter || '',
            stockStatus: profile.stockStatus || '',
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const url = editingProfile
                ? `${API_BASE}/api/profile-catalog/${editingProfile._id}`
                : `${API_BASE}/api/profile-catalog`;
            const method = editingProfile ? 'PUT' : 'POST';
            const body = {
                ...formData,
                weightPerMeter: parseInt(formData.weightPerMeter) || 0,
            };
            const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'שגיאה');
            setShowModal(false);
            fetchProfiles();
        } catch (err) {
            alert(err.message);
        }
        setSaving(false);
    };

    const handleDelete = async (profile) => {
        if (!window.confirm(`למחוק את פרופיל ${profile.pnum} - ${profile.description}?`)) return;
        try {
            const res = await fetch(`${API_BASE}/api/profile-catalog/${profile._id}`, { method: 'DELETE', headers });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error);
            }
            fetchProfiles();
        } catch (err) {
            alert(err.message);
        }
    };

    const canEdit = (profile) => {
        if (isAdmin) return true;
        if (profile.source === 'user' && profile.createdBy?._id === currentUser?.id) return true;
        return false;
    };

    const canDelete = () => isAdmin;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" dir="rtl">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 sticky top-[72px] lg:top-[88px] z-40 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <Link to="/professionals" className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-700 mb-2 transition-colors font-bold text-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                                חזרה לכלים
                            </Link>
                            <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
                                קטלוג פרופילים
                            </h1>
                            <p className="text-slate-500 mt-1">
                                {total} פרופילים • צפייה בנתונים, נוסחאות חיתוך, תמונות ומשקלים
                            </p>
                        </div>
                        <div className="flex gap-3 items-center">
                            <div className="relative">
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </span>
                                <input
                                    type="text"
                                    placeholder="חיפוש מק''ט או שם..."
                                    value={searchQuery}
                                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                                    className="w-full md:w-64 pl-4 pr-10 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all shadow-sm font-medium"
                                />
                            </div>
                            <button
                                onClick={openAddModal}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg whitespace-nowrap"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                </svg>
                                הוסף פרופיל
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col lg:flex-row gap-8 items-start">
                {/* Sidebar */}
                <div className="w-full lg:w-64 flex-shrink-0 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 lg:sticky lg:top-[200px]">
                    <h2 className="font-black text-slate-800 text-lg mb-4 px-2 border-b border-slate-100 pb-3">סדרות ({models.length})</h2>
                    <div className="flex flex-col gap-1 max-h-[60vh] overflow-y-auto pl-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-slate-200 [&::-webkit-scrollbar-thumb]:rounded-full pr-1">
                        {models.map(m => (
                            <button
                                key={m.model}
                                onClick={() => { setSelectedModel(m.model); setPage(1); setSelectedSubCat('all'); }}
                                className={`text-right w-full px-4 py-3 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-between ${
                                    selectedModel === m.model
                                        ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                                        : 'bg-transparent text-slate-600 border border-transparent hover:bg-slate-50 hover:text-blue-600'
                                }`}
                            >
                                <span className="truncate max-w-[75%]">{m.model}</span>
                                <span className="text-xs text-slate-400 font-mono">{m.count}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Grid */}
                <div className="flex-1 w-full min-w-0">
                    {/* Sub-Category Chips */}
                    <div className="sticky top-[204px] z-30 bg-slate-50/95 backdrop-blur-md pb-4 pt-2 -mx-2 px-2 border-b border-slate-200 mb-6 flex flex-wrap gap-2 shadow-sm rounded-t-2xl lg:shadow-none">
                        {subCategories.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedSubCat(cat.id)}
                                className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all duration-300 ${
                                    selectedSubCat === cat.id
                                        ? 'bg-slate-800 text-white shadow-md'
                                        : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800'
                                }`}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="text-center py-20">
                            <div className="inline-block w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            <p className="text-slate-500 mt-4 font-bold">טוען פרופילים...</p>
                        </div>
                    ) : filteredProfiles.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200 shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-slate-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 className="text-xl font-bold text-slate-700">לא נמצאו פרופילים</h3>
                            <p className="text-slate-500 mt-2">נסה לחפש מק''ט או תיאור אחר.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredProfiles.map((p) => {
                                const paddedPnum = p.pnum.padStart(5, '0');
                                const imagePath = `/catalog_images/${p.image || `C${paddedPnum}.jpg`}`;
                                const hasFormulas = p.formulas && p.formulas.length > 0;
                                const isExpanded = expandedFormulas[p.pnum];

                                return (
                                    <div key={p._id} className="group flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-slate-300 transition-all duration-500 overflow-hidden">
                                        {/* Source Badge */}
                                        {p.source === 'user' && (
                                            <div className="absolute top-2 left-2 z-10 bg-purple-100 text-purple-700 text-[9px] font-black px-2 py-0.5 rounded-full">
                                                משתמש
                                            </div>
                                        )}
                                        {p.source === 'erp' && (
                                            <div className="absolute top-2 left-2 z-10 bg-amber-100 text-amber-700 text-[9px] font-black px-2 py-0.5 rounded-full">
                                                ERP
                                            </div>
                                        )}

                                        {/* Image */}
                                        <div className="relative h-48 bg-slate-50 p-4 border-b border-slate-100 flex items-center justify-center overflow-hidden cursor-pointer" onClick={() => setSelectedImage(imagePath)}>
                                            <div className="absolute inset-0 bg-blue-900/0 group-hover:bg-blue-900/5 transition-colors duration-300 z-10 flex items-center justify-center">
                                                <div className="opacity-0 group-hover:opacity-100 bg-white text-blue-900 font-bold px-4 py-2 rounded-full shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 text-xs flex items-center gap-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                    </svg>
                                                    VIEW
                                                </div>
                                            </div>
                                            <img src={imagePath} alt={p.description} onError={handleImageError} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500" />
                                        </div>

                                        {/* Content */}
                                        <div className="p-5 flex-1 flex flex-col">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">
                                                        מק''ט: {p.pnum}
                                                    </div>
                                                    <h3 className="font-bold text-slate-900 text-lg leading-tight line-clamp-2">
                                                        {p.description}
                                                    </h3>
                                                </div>
                                                {/* Edit/Delete Controls */}
                                                {canEdit(p) && (
                                                    <div className="flex gap-1 mr-2 flex-shrink-0">
                                                        <button onClick={() => openEditModal(p)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="עריכה">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                        {canDelete(p) && (
                                                            <button onClick={() => handleDelete(p)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="מחיקה">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Weight */}
                                            <div className="mt-2 flex items-center text-sm">
                                                <div className="flex flex-col">
                                                    <span className="text-xs text-slate-500 font-medium">משקל (גרם/מ')</span>
                                                    <span className="font-bold text-slate-800 tracking-tight">
                                                        {p.weightPerMeter > 0 ? p.weightPerMeter : <span className="text-red-500 text-xs">חסר</span>}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Formula Section */}
                                            <div className="mt-3 pt-3 border-t border-slate-100">
                                                {hasFormulas ? (
                                                    <div>
                                                        <button
                                                            onClick={() => toggleFormulas(p.pnum)}
                                                            className="w-full flex items-center justify-between text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors"
                                                        >
                                                            <span>נוסחאות חיתוך ({p.formulas.length})</span>
                                                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </button>
                                                        {isExpanded && (
                                                            <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto">
                                                                {p.formulas.map((f, i) => (
                                                                    <div key={i} className="bg-slate-50 p-2 rounded-lg text-[10px] border border-slate-100">
                                                                        <div className="font-bold text-slate-600 truncate mb-0.5" title={f.productName}>
                                                                            {f.productName || `COD ${f.productCod}`}
                                                                        </div>
                                                                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-slate-500">
                                                                            <span>אורך: <span className="font-mono font-bold text-blue-700">{f.lengthFormula || '—'}</span></span>
                                                                            {f.kLength !== 0 && <span>K={f.kLength}</span>}
                                                                            <span>כיוון: <span className="font-bold">{f.direction || '—'}</span></span>
                                                                            <span>חיתוך: <span className="font-bold">{f.cutType || '—'}</span></span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="text-xs font-bold text-red-500 flex items-center gap-1">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                                        </svg>
                                                        חסר נוסחאות חיתוך
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="grid grid-cols-2 divide-x divide-x-reverse divide-slate-100 border-t border-slate-100">
                                            <a href={`/catalog_files/${paddedPnum}.dwg`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 py-3 text-sm font-bold text-slate-600 hover:text-blue-600 hover:bg-slate-50 transition-colors">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                                </svg>
                                                DWG
                                            </a>
                                            <a href={`/catalog_files/${paddedPnum}.pdf`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 py-3 text-sm font-bold text-slate-600 hover:text-red-600 hover:bg-slate-50 transition-colors">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                                PDF
                                            </a>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Image Modal */}
            {selectedImage && (
                <div className="fixed inset-0 z-50 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10" onClick={() => setSelectedImage(null)}>
                    <button className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all duration-300" onClick={() => setSelectedImage(null)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    <div className="bg-white rounded-3xl overflow-hidden max-w-4xl max-h-full flex items-center justify-center p-8 shadow-2xl" onClick={e => e.stopPropagation()}>
                        <img src={selectedImage} alt="Profile Full View" className="max-w-full max-h-[80vh] object-contain" onError={(e) => { e.target.src = '/assets/logo-full.png'; }} />
                    </div>
                </div>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex justify-between items-center">
                            <h3 className="font-black text-slate-800 text-lg">{editingProfile ? 'עריכת פרופיל' : 'הוספת פרופיל חדש'}</h3>
                            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">מק"ט (PNUM) *</label>
                                    <input
                                        type="text"
                                        value={formData.pnum}
                                        onChange={e => setFormData(f => ({ ...f, pnum: e.target.value }))}
                                        disabled={!!editingProfile}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none disabled:bg-slate-100 disabled:text-slate-500 font-mono"
                                        placeholder="04911"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">משקל (גרם/מ')</label>
                                    <input
                                        type="number"
                                        value={formData.weightPerMeter}
                                        onChange={e => setFormData(f => ({ ...f, weightPerMeter: e.target.value }))}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                        placeholder="323"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">תיאור *</label>
                                <input
                                    type="text"
                                    value={formData.description}
                                    onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                    placeholder="כנף זיגוג 10-16 מ'מ"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">סדרה / מודל</label>
                                    <input
                                        type="text"
                                        value={formData.model}
                                        onChange={e => setFormData(f => ({ ...f, model: e.target.value }))}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                        placeholder="קליל 1700 בלגי"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">מצב מלאי</label>
                                    <select
                                        value={formData.stockStatus}
                                        onChange={e => setFormData(f => ({ ...f, stockStatus: e.target.value }))}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none bg-white"
                                    >
                                        <option value="">לא ידוע</option>
                                        <option value="במלאי">במלאי</option>
                                        <option value="מינימום הזמנה 250 ק''ג">מינימום הזמנה 250 ק''ג</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setShowModal(false)} className="px-4 py-2 text-slate-600 hover:text-slate-800 font-bold text-sm">ביטול</button>
                            <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-sm shadow-md disabled:opacity-50 transition-all">
                                {saving ? 'שומר...' : (editingProfile ? 'שמור שינויים' : 'הוסף פרופיל')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
